import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionPlans } from '../hooks/useSubscriptionPlans';
import './Pricing.css';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const Pricing = () => {
  const navigate = useNavigate();
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [emails, setEmails] = useState({}); // Store email for each plan
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailPopup, setShowEmailPopup] = useState(null); // Track which button showed popup
  const [billingConfig, setBillingConfig] = useState({
    freeModeEnabled: false,
    freeModePlanId: 'free',
    freeModeTrialDays: 60,
    freeModeMessage: ''
  });
  const [configLoading, setConfigLoading] = useState(true);
  
  // Fetch dynamic subscription plans
  const { plans: dynamicPlans, loading: plansLoading, error: plansError } = useSubscriptionPlans();

  // Get correct API base URL (same logic as api/client.js)
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    } else {
      // Use proxy approach for better security - Vercel forwards /api/* to backend
      return '/api';
    }
  };

  useEffect(() => {
    const loadBillingConfig = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/onboarding/environment-status`);
        if (!response.ok) {
          throw new Error('Failed to load environment status');
        }
        const result = await response.json();
        const freeMode = result?.free_mode || {};
        setBillingConfig({
          freeModeEnabled: Boolean(freeMode.enabled),
          freeModePlanId: freeMode.plan_id || 'free',
          freeModeTrialDays: typeof freeMode.trial_days === 'number' ? freeMode.trial_days : 60,
          freeModeMessage: freeMode.message || ''
        });
      } catch (err) {
        console.error('Unable to load billing configuration', err);
      } finally {
        setConfigLoading(false);
      }
    };

    loadBillingConfig();
  }, []);

  // Helper functions for plan display (defined before useMemo)
  const getDescriptionForPlan = React.useCallback((planKey) => {
    const descriptions = {
      basic: 'Perfect for small clinics getting started',
      pro: 'Most popular for growing practices',
      enterprise: 'For large clinics with advanced needs'
    };
    return descriptions[planKey] || '';
  }, []);
  
  const getDefaultFeaturesForPlan = React.useCallback((planKey) => {
    const defaultFeatures = {
      basic: [
        'Up to 1,000 clients',
        'Email automation campaigns', 
        'Basic reporting & analytics',
        'Pabau integration',
        'Email support',
        '14-day free trial'
      ],
      pro: [
        'Up to 10,000 clients',
        'Advanced automation workflows',
        'Detailed analytics & insights', 
        'A/B testing campaigns',
        'Priority email support',
        'Custom integrations',
        '14-day free trial'
      ],
      enterprise: [
        'Unlimited clients',
        'Custom automation workflows',
        'Advanced reporting suite',
        'Dedicated account manager', 
        'Phone & email support',
        'Custom onboarding',
        'SLA guarantees',
        '14-day free trial'
      ]
    };
    return defaultFeatures[planKey] || [];
  }, []);

  // Convert dynamic plans to the expected format, with fallback
  const plans = React.useMemo(() => {
    if (!dynamicPlans) return {};
    
    // Map dynamic plans to display format
    const convertedPlans = {};
    
    // Only show plans that should be visible on pricing page (exclude free and internal)
    const visiblePlans = ['basic', 'pro', 'enterprise'];
    
    visiblePlans.forEach(planKey => {
      const plan = dynamicPlans[planKey];
      if (plan && plan.is_active) {
        convertedPlans[planKey] = {
          name: plan.display_name || plan.name,
          description: getDescriptionForPlan(planKey),
          monthlyPrice: plan.monthly_price,
          annualPrice: plan.annual_price,
          features: (Array.isArray(plan.features) && plan.features.length > 0) ? plan.features : getDefaultFeaturesForPlan(planKey),
          highlighted: planKey === 'pro', // Pro is always highlighted
          cta: 'Start Free Trial'
        };
      }
    });
    
    return convertedPlans;
  }, [dynamicPlans, getDescriptionForPlan, getDefaultFeaturesForPlan]);

  const handleStartTrial = async (planId) => {
    setSelectedPlan(planId);
    setError('');
    setShowEmailPopup(null); // Clear any existing popup
    
    const email = emails[planId] || '';
    
    // Check if email is provided
    if (!email || !email.includes('@')) {
      // Show popup for this specific plan button
      setShowEmailPopup(planId);
      // Auto-hide popup after 4 seconds
      setTimeout(() => setShowEmailPopup(null), 4000);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create signup lead with email and selected plan
      const apiUrl = `${getApiBaseUrl()}/onboarding/signup-lead`;
      console.log('🔗 Making API call to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim(),
          clinic_name: '', // Will be collected later
          password: '', // Will be collected later
          contact_phone: '',
          timezone: 'America/New_York', // Default, will be updated later
          currency: 'GBP',
          selected_plan: planId,
          billing_period: billingPeriod
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Navigate to check inbox page with lead ID
        navigate(`/check-inbox?lead_id=${result.lead_id}&email=${encodeURIComponent(email)}&plan=${planId}`);
      } else {
        setError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPrice = (plan) => {
    const amount = billingPeriod === 'monthly' ? plan.monthlyPrice : Math.round(plan.annualPrice / 12);
    return amount;
  };

  const getSavings = (plan) => {
    if (billingPeriod === 'annual') {
      const annualSavings = (plan.monthlyPrice * 12) - plan.annualPrice;
      return Math.round((annualSavings / (plan.monthlyPrice * 12)) * 100);
    }
    return 0;
  };

  // Show loading state
  if (plansLoading || configLoading) {
    return (
      <div className="pricing-page">
        <div className="pricing-header">
          <div className="container">
            <h1>Loading Pricing...</h1>
            <p>Fetching the latest pricing information...</p>
          </div>
        </div>
      </div>
    );
  }

  const isFreeMode = billingConfig.freeModeEnabled;

  if (isFreeMode) {
    const freePlanId = billingConfig.freeModePlanId || 'free';
    const trialDays = billingConfig.freeModeTrialDays || 60;
    const trialMessage = trialDays
      ? `Enjoy ${trialDays}-day complimentary access with full MedInsight capabilities.`
      : 'Enjoy complimentary access with full MedInsight capabilities.';
    const freeModeMessage =
      billingConfig.freeModeMessage ||
      'We are waiving billing during launch—activate your workspace and explore everything MedInsight offers.';
    const freePlanFeatures = [
      'Full CRM marketing automation during the complimentary period',
      'Instant tenant provisioning and Pabau integration hooks',
      'Campaign templates, analytics, and automation workflows included',
      'Upgrade to a paid plan whenever billing resumes—no downtime'
    ];
    const currencySymbol = getCurrencySymbol('GBP');

    return (
      <div className="pricing-page">
        <div className="pricing-header">
          <div className="container">
            <h1>Complimentary Access</h1>
            <p>{freeModeMessage}</p>
          </div>
        </div>

        <div className="pricing-cards">
          <div className="container">
            <div className="cards-grid">
              <div className="pricing-card highlighted">
                <div className="card-header">
                  <h3>MedInsight Free Plan</h3>
                  <p>{trialMessage}</p>
                </div>

                <div className="card-pricing">
                  <div className="price">
                    <span className="amount">{`${currencySymbol}0`}</span>
                    <span className="period">/launch promo</span>
                  </div>
                </div>

                <div className="card-features">
                  <ul>
                    {freePlanFeatures.map((feature, index) => (
                      <li key={index}>
                        <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-action">
                  <div className="email-capture">
                    <input
                      type="email"
                      placeholder="Enter your company email"
                      value={emails[freePlanId] || ''}
                      onChange={(e) => {
                        setEmails((prev) => ({
                          ...prev,
                          [freePlanId]: e.target.value
                        }));
                        if (showEmailPopup) {
                          setShowEmailPopup(null);
                        }
                      }}
                      className="email-input"
                      disabled={isLoading}
                    />
                    <div className="button-container">
                      <button
                        className="cta-button primary"
                        onClick={() => handleStartTrial(freePlanId)}
                        disabled={isLoading}
                      >
                        {isLoading && selectedPlan === freePlanId ? 'Starting...' : 'Activate Free Plan'}
                      </button>
                      {showEmailPopup === freePlanId && (
                        <div className="email-popup">
                          <div className="popup-content">
                            <span className="popup-icon">📧</span>
                            <span className="popup-text">Please enter your email address to start your free trial</span>
                          </div>
                          <div className="popup-arrow"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <div className="container">
                <div className="error-content">
                  <p>{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="pricing-faq">
          <div className="container">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-grid">
              <div className="faq-item">
                <h3>What do I get during the complimentary period?</h3>
                <p>Everything. You can provision your tenant, sync Pabau data, and run automation campaigns without restrictions.</p>
              </div>
              <div className="faq-item">
                <h3>Will my workspace stop when billing resumes?</h3>
                <p>No. We will give you plenty of notice and a one-click upgrade path before charging resumes.</p>
              </div>
              <div className="faq-item">
                <h3>Do I still need to connect Pabau?</h3>
                <p>Yes, the onboarding experience still captures your Pabau credentials so we can automate syncs for you.</p>
              </div>
              <div className="faq-item">
                <h3>Who can I contact for help?</h3>
                <p>The MedInsight team is on standby—reach out via support chat or email and we’ll guide you through onboarding.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="pricing-trust">
          <div className="container">
            <div className="trust-indicators">
              <div className="trust-item">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Complimentary full access</span>
              </div>
              <div className="trust-item">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>No card required</span>
              </div>
              <div className="trust-item">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span>Cancel anytime</span>
              </div>
              <div className="trust-item">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
                </svg>
                <span>Priority onboarding support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state with fallback
  if (plansError && Object.keys(plans).length === 0) {
    return (
      <div className="pricing-page">
        <div className="pricing-header">
          <div className="container">
            <h1>Pricing Information</h1>
            <p style={{ color: '#ff6b6b' }}>
              Unable to load current pricing. Please contact support for assistance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      {/* Header */}
      <div className="pricing-header">
        <div className="container">
          <h1>Simple, Transparent Pricing</h1>
          <p>Choose the perfect plan for your clinic. Start with a 14-day free trial, no credit card required.</p>
          
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span className={billingPeriod === 'monthly' ? 'active' : ''}>Monthly</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={billingPeriod === 'annual'}
                onChange={(e) => setBillingPeriod(e.target.checked ? 'annual' : 'monthly')}
              />
              <span className="slider"></span>
            </label>
            <span className={billingPeriod === 'annual' ? 'active' : ''}>
              Annual
              <span className="savings-badge">Save 17%</span>
            </span>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="pricing-cards">
        <div className="container">
          <div className="cards-grid">
            {Object.entries(plans).map(([planId, plan]) => (
              <div key={planId} className={`pricing-card ${plan.highlighted ? 'highlighted' : ''}`}>
                {plan.highlighted && <div className="popular-badge">Most Popular</div>}
                
                <div className="card-header">
                  <h3>{plan.name}</h3>
                  <p>{plan.description}</p>
                </div>

                <div className="card-pricing">
                  <div className="price">
                    <span className="amount">{formatCurrency(getPrice(plan))}</span>
                    <span className="period">/{billingPeriod === 'monthly' ? 'mo' : 'mo'}</span>
                  </div>
                  {billingPeriod === 'annual' && (
                    <div className="annual-savings">
                      Save {getSavings(plan)}% annually
                    </div>
                  )}
                  {billingPeriod === 'annual' && (
                    <div className="billed-annually">
                      Billed annually ({formatCurrency(plan.annualPrice)})
                    </div>
                  )}
                </div>

                <div className="card-features">
                  <ul>
                    {(Array.isArray(plan.features) ? plan.features : []).map((feature, index) => (
                      <li key={index}>
                        <svg className="check-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-action">
                  <div className="email-capture">
                    <input
                      type="email"
                      placeholder="Enter your company email"
                      value={emails[planId] || ''}
                      onChange={(e) => {
                        setEmails(prev => ({
                          ...prev,
                          [planId]: e.target.value
                        }));
                        // Hide popup when user starts typing
                        if (showEmailPopup) {
                          setShowEmailPopup(null);
                        }
                      }}
                      className="email-input"
                      disabled={isLoading}
                    />
                    <div className="button-container">
                      <button
                        className={`cta-button ${plan.highlighted ? 'primary' : 'secondary'}`}
                        onClick={() => handleStartTrial(planId)}
                        disabled={isLoading}
                      >
                        {isLoading && selectedPlan === planId ? 'Starting...' : plan.cta}
                      </button>
                      
                      {/* Email Required Popup */}
                      {showEmailPopup === planId && (
                        <div className="email-popup">
                          <div className="popup-content">
                            <span className="popup-icon">📧</span>
                            <span className="popup-text">Please enter your email address to start your free trial</span>
                          </div>
                          <div className="popup-arrow"></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="error-message">
            <div className="container">
              <div className="error-content">
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* FAQ Section */}
      <div className="pricing-faq">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>What's included in the free trial?</h3>
              <p>Full access to all features for 14 days. Import your Pabau data and test all automation features with no limitations.</p>
            </div>
            <div className="faq-item">
              <h3>Do I need a Pabau account?</h3>
              <p>Yes, MedInsight Pro integrates with Pabau to sync your client data and automate your marketing campaigns.</p>
            </div>
            <div className="faq-item">
              <h3>Can I change plans later?</h3>
              <p>Absolutely! Upgrade or downgrade anytime. Changes take effect on your next billing cycle.</p>
            </div>
            <div className="faq-item">
              <h3>What support do you offer?</h3>
              <p>All plans include email support. Pro and Enterprise plans get priority support with faster response times.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="pricing-trust">
        <div className="container">
          <div className="trust-indicators">
            <div className="trust-item">
              <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>14-day free trial</span>
            </div>
            <div className="trust-item">
              <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Secure & encrypted</span>
            </div>
            <div className="trust-item">
              <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span>Cancel anytime</span>
            </div>
            <div className="trust-item">
              <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
              </svg>
              <span>24/7 support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing; 
